// Percorso file: src\pages\UniformDistribution.jsx

import React, { useEffect, useMemo, useState } from 'react';
// ⚠️ L'ORDINE DEI DUE FOGLI CONTA, ed è quello di UsersList: il
// foglio CONDIVISO prima, quello della pagina dopo, così dove due regole
// pesassero uguale vince quella di pagina. `AdminTools.css` è il kit degli
// strumenti admin (doc 09 §4): da UNIFORMS-UI/Fetta A questa pagina ne usa
// i campi della barra (`qpsc-field`, `qpsc-input`, `qpsc-select`), i
// pulsanti compatti (`qpsc-tool-btn`) e le tendine di riga a classe
// ripetuta. Il foglio non porta alcuna regola su selettori nudi — solo
// classi `.qpsc-*` — quindi il resto della pagina non ne risente.
import '../components/AdminTools.css';
import './UniformDistribution.css';
// IL TELAIO CONDIVISO (doc 07 §7, doc 09 §3): barra utente, banda «‹ Back»
// / «Switch role», titolo centrato, colonna «admin» a tutta finestra sopra i
// 768px. È il calco di TeamAssignment e di UsersList.
import HomeShell from '../components/HomeShell';
import UniformConfig from '../components/UniformConfig';
// IL REPORT «Stats» (UNIFORMS-UI/Fetta STATS): una modale di sola vista che
// si calcola dai dati già in pagina (utenti + catalogo dell'anno guardato).
import UniformReport from '../components/UniformReport';
import { api } from '../api/api';
// `hasRole` è la scorciatoia del gate unico (auth/access.js via roleUtils):
// «è SUPERADMIN?» si chiede così, mai con un confronto a mano — è la forma
// di TeamAssignment e della scheda del socio.
import { canonicalRole, hasRole } from '../utils/roleUtils';
// Etichetta leggibile del ruolo per la colonna Role (P9, poi UNIFORMS/D7):
// a schermo non deve comparire il valore canonico maiuscolo.
//
// ⚠️ Due sedi d'importazione nello stesso file — `canonicalRole` da
// roleUtils (che lo ri-esporta) e `roleLabel` da access.js — perché
// `roleLabel` in roleUtils non c'è. Non si aggiunge qui: roleUtils è fuori
// dal perimetro. È la forma già in opera in TeamAssignment, in Messaging e in
// UsersList, che importano `roleLabel` da access.js allo stesso modo.
import { roleLabel } from '../auth/access';
// LA SEDE UNICA DELLE DIVISE (UNIFORMS/D1, D7, poi UNIFORMS-UI/Fetta B): le
// etichette dei tre stati, il criterio di spettanza e — dalla Fetta B — il
// RIPIEGO della lista dei capi (`capiDelCatalogo`: `items` del catalogo se
// c'è, altrimenti gli otto storici), i soli capi attivi in ordine
// (`capiAttivi`) e la lettura di `hasSize` (`capoHaTaglia`). Qui si
// importano, non si ridefiniscono: questa pagina non legge più `TIPI_DIVISA`.
// Dal cantiere UNIFORMS-ANNO (Fetta 3) anche le CONSEGNE si leggono da lì:
// `consegneDellAnnata` (la mappa delle celle dell'anno guardato) e
// `sedeDelleConsegne` (DOVE il backend le scrive: la fetta dell'anno o il
// piatto del 2026 non battezzato). Questa pagina non legge mai
// `user.uniforms` / `user.uniformsSeasons` per conto suo.
import {
  ETICHETTE_STATO,
  tipiDivisaSpettanti,
  capiDelCatalogo,
  capiAttivi,
  capoHaTaglia,
  consegneDellAnnata,
  sedeDelleConsegne
} from '../utils/uniforms';
// LA SEDE UNICA DELLA LETTURA DI SQUADRA E DELL'ANNO (cantiere «lettore
// unico»). I primi due nomi sono quelli che importa UsersList,
// stessi corpi; gli altri due servono alla sola CELLA Team (Fetta A, vedi
// `testoSquadreInCella`): questa pagina non riscrive alcun predicato di
// tessera.
import {
  tessereDelSoggetto,
  squadreDelSoggetto,
  squadreDelleTessere,
  primaSquadraDellaTessera
} from '../utils/squadre';
import { annoDiLista } from '../utils/stagione';
// I due anni del club e lo stato della loro lettura vivono nella sede
// condivisa (AuthContext, YS-5), già normalizzati a intero: nessuna chiamata
// propria a `GET /season-config`. È la stessa lettura di UsersList
// e della scheda del socio (`MemberProfileModal.jsx`).
import { useAuth } from '../context/AuthContext';

const sortStrings = (a, b) => (a > b ? 1 : a < b ? -1 : 0);

// Le taglie della tendina, senza voce vuota (D9A, RD-168(a)): la stringa
// vuota in coda diventava una riga BIANCA nella tendina. «Nessuna taglia» è
// già il segnaposto `<option value="">Size</option>`, e il report aggiunge da
// sé la colonna «(no size)» (`SENZA_TAGLIA`, utils/statistiche-divise.js).
const sizes = [
  '5XS', '4XS', '3XS', '2XS', 'XS',
  'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'
];

// Le tre voci della tendina di stato. I VALORI sono quelli dei documenti, le
// ETICHETTE vengono dalla sede unica (`utils/uniforms.js`): «NO» locale è
// caduto, si legge «N/A» qui come nella scheda del socio.
const statusOptions = [
  { value: 'not_delivered', label: ETICHETTE_STATO.not_delivered },
  { value: 'delivered', label: ETICHETTE_STATO.delivered },
  { value: 'no', label: ETICHETTE_STATO.no }
];

// ⚠️ CHI COMPARE IN QUESTA PAGINA (UNIFORMS/D2): chi ha almeno una tessera
// PLAYER o COACH valida dell'ANNO GUARDATO. Non più «status ACTIVE di persona
// e ruolo diverso da PARENT» (il vecchio `compareInElenco`), che leggeva due
// CAMPI DI PERSONA e sbagliava in due modi opposti: teneva dentro gli admin
// che non allenano — tutte le celle vuote — e ci teneva anche chi era
// tesserato in un'altra annata.
const RUOLI_IN_ELENCO = ["PLAYER", "COACH"];

// VOCE-SENTINELLA DEL FILTRO SQUADRA: «solo chi allena» (UNIFORMS-UI/Fetta A,
// conferma C). Calco di `SENZA_SQUADRA = "__NO_TEAM__"` in Team Assignment
// (`TeamAssignment.jsx`): la difesa contro una squadra omonima è la FORMA
// del valore — doppio underscore, maiuscolo, senza spazi: non è un nome che
// un amministratore digiterebbe — e la sentinella si controlla PRIMA del
// confronto a maiuscolo coi nomi. Non può essere la parola
// nuda «COACHES»: una squadra chiamata «Coaches» la incontrerebbe. A schermo
// la voce dice «Coaches».
const SOLO_COACH = "__COACHES__";

// Il catalogo assente non è un guasto (UNIFORMS/Fetta A): finché nessun admin
// lo salva e nessuna squadra nasce, `GET /uniform-config` risponde 404 e
// l'annata non ha catalogo. Si rende come catalogo VUOTO — tabella senza
// celle — che è la verità, ed è lo stato da cui la modale parte. `items:
// null` = nessuna lista salvata: le colonne sono gli otto capi storici
// (ripiego della sede unica, Fetta B).
const CATALOGO_VUOTO = { roles: {}, teams: {}, items: null };

// TESTI A SCHERMO (inglese, CLAUDE.md §3.7). Il testo del 409 è quello della
// scheda del socio (`MemberProfileModal.jsx`), verbatim: è lo stesso
// guasto — la cassettina della stagione non si legge — e dirlo con due frasi
// diverse in due pagine lo farebbe sembrare due guasti.
const MSG_STAGIONE = "Could not read the club season. Please try again in a moment.";
const MSG_ATTESA = "Loading uniforms…";
const MSG_CONFIG = "Could not load the uniform catalogue. Please try again in a moment.";
const MSG_SALVATAGGIO = "Could not save this change. The previous value has been restored.";
// L'elenco non letto (D9A, RD-168(b)): testo di User List (`UsersList.jsx`,
// `fetchUsers`), verbatim — è lo stesso guasto.
const MSG_UTENTI = "Could not load the member list. Please try again.";

// LE DUE ETICHETTE DELLA CELLA TEAM che non sono un nome di squadra
// (UNIFORMS-UI/Fetta A, decisione del committente). «All teams» è già la
// dizione di Team Assignment; «Many teams» ne segue la forma (§3.2: maiuscola
// solo sulla prima parola). Il separatore fra due nomi è il punto mediano
// (§3.3, come la colonna Role).
const CELLA_TUTTE = "All teams";
const CELLA_MOLTE = "Many teams";
const SOGLIA_MOLTE = 3;

/*
 * IL TESTO DELLA CELLA TEAM — sola RESA, mai un dato (UNIFORMS-UI/Fetta A,
 * punto 3 + split).
 *
 * Regola del committente: la tessera PLAYER porta la SUA squadra (una sola:
 * `primaSquadraDellaTessera`, l'invariante del club, `utils/squadre.js`);
 * la tessera COACH porta le sue squadre — uno o due nomi uniti dal punto
 * mediano, «Many teams» da tre in su, «All teams» se ha il permesso «tutte
 * le squadre». Chi è player E coach nello stesso anno resta su UNA riga (lo
 * split a due righe è la Fetta C) e la cella applica la regola unita: le
 * squadre delle due tessere si sommano, e su quella somma valgono le stesse
 * soglie.
 *
 * ⚠️ È SOLO IL TESTO DELLA CELLA. Il filtro di squadra, la tendina e
 * l'ordinamento leggono `riga.squadre` — l'insieme dei nomi — e non questo
 * testo: scegliendo «U12 Boys» rispondono anche i coach che stanno sotto a
 * «Many teams» o «All teams». Per questo la funzione non tocca `riga.squadre`
 * e nasce dalle tessere.
 *
 * ⚠️ Perché le squadre del PLAYER non passano da `squadreDelleTessere`: quella
 * unione onora `allTeams` su qualunque tessera, mentre «tutte le squadre» è un
 * permesso da allenatore (`squadre.js`, differenza D7). Un giocatore con una
 * tessera storica `approvedTeams:["ALL"]` mostra quindi la squadra che ha, non
 * «All teams».
 *
 * Il confronto fra nomi è quello della pagina (`chiaveSquadra`): «U12» e
 * «u12» sono un nome solo, e a schermo resta la prima grafia incontrata.
 */
const testoSquadreInCella = (tessere) => {
  const elenco = Array.isArray(tessere) ? tessere : [];

  const daCoach = squadreDelleTessere(
    elenco.filter((ctx) => canonicalRole(ctx?.role) === "COACH"),
    { assorbeTutte: false, doppioni: "normalizzati" }
  );

  if (daCoach.tutte) return CELLA_TUTTE;

  const nomi = [];
  const aggiungi = (nome) => {
    const pulito = String(nome || "").trim();
    if (pulito === "") return;
    const chiave = pulito.toUpperCase();
    if (!nomi.some((gia) => gia.toUpperCase() === chiave)) nomi.push(pulito);
  };

  elenco
    .filter((ctx) => canonicalRole(ctx?.role) === "PLAYER")
    .forEach((ctx) => aggiungi(primaSquadraDellaTessera(ctx)));
  daCoach.nomi.forEach(aggiungi);

  if (nomi.length >= SOGLIA_MOLTE) return CELLA_MOLTE;
  return nomi.join(' · ') || '-';
};

/*
 * LA BARRA DEI FILTRI — tre campi nel guscio condiviso `.qpsc-field` con
 * l'etichetta sopra (doc 07 §8, §8.1; `AdminTools.css`), come la
 * barra di User List (`UsersList.jsx`). Il filtro «Roles» non c'è
 * più (UNIFORMS-UI/Fetta A, punto 5): l'ordinamento per colonna Role legge
 * `riga.ruoli` e non ne aveva bisogno.
 */
const UniformFilters = ({
  search,
  setSearch,
  stagioneAttiva,
  setStagioneScelta,
  stagioniDisponibili,
  selectedTeam,
  setSelectedTeam,
  uniqueTeams
}) => (
  <div className="ud-toolbar">
    <div className="qpsc-field">
      <span className="qpsc-field__label">Search</span>
      <input
        className="qpsc-input"
        type="text"
        aria-label="Search by name"
        placeholder="Search by name"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
    </div>
    {/* IL SELETTORE DI STAGIONE (UNIFORMS/D3).
        Sta in prima posizione fra le tendine perché non è un filtro fra i
        filtri: sceglie la COORTE su cui l'altro lavora. La squadra
        restringe un insieme; la stagione dice quale sia quell'insieme.

        ⚠️ FORMA E VESTE SONO QUELLE DI USER LIST (`UsersList.jsx`),
        prese alla lettera — stesse classi, stessa etichetta «Season Viewed»,
        stesso valore stringa nel DOM e numero nello stato, stesso `onChange`.
        Là il selettore NON è un componente estratto ma JSX in linea: non
        esistendo un componente da importare, si riusa la forma e il foglio di
        stile condiviso.

        ⚠️ Il nome è «Season Viewed» e non «Season»: dice che cosa questa
        pagina sta guardando, non come sta il club. */}
    <div className="qpsc-field">
      <span className="qpsc-field__label">Season Viewed</span>
      <select
        className="qpsc-select"
        aria-label="Season viewed"
        value={stagioneAttiva === null ? '' : String(stagioneAttiva)}
        onChange={e => setStagioneScelta(Number(e.target.value))}
      >
        {stagioniDisponibili.map(anno => (
          <option key={anno} value={String(anno)}>{anno}</option>
        ))}
      </select>
    </div>
    <div className="qpsc-field">
      <span className="qpsc-field__label">Team</span>
      <select
        className="qpsc-select"
        aria-label="Filter by team"
        value={selectedTeam}
        onChange={e => setSelectedTeam(e.target.value)}
      >
        <option value="">All Teams</option>
        {/* «Coaches» è una CONDIZIONE, non una squadra (calco di
            «— Unassigned —» in Team Assignment): risponde chi
            ha almeno una tessera COACH dell'anno guardato. Il valore è la
            sentinella `SOLO_COACH`, mai un nome. */}
        <option value={SOLO_COACH}>Coaches</option>
        {uniqueTeams.map(team => (
          <option key={team} value={team}>{team}</option>
        ))}
      </select>
    </div>
  </div>
);

/*
 * LE CHIAVI D'ORDINAMENTO — su valori DERIVATI, non sui campi di persona.
 *
 * ⚠️ Il vecchio `getSortValue` leggeva `user['role']` e `user['team']`
 * GREZZI: la colonna Role ordinava «Child» fra «Admin» e «Coach» invece che
 * sotto Player, e la colonna Team ordinava su un campo che questa pagina non
 * mostra più. Ora le due chiavi vengono dalla riga già calcolata sull'annata.
 *
 * ⚠️ `CHIAVE_TUTTE_LE_SQUADRE` e il «chi non ha squadre va in fondo» sono di
 * UsersList, copiati per la stessa ragione dichiarata là: con
 * `sortStrings` da solo la stringa vuota ordina PRIMA di ogni nome, e la
 * prima schermata dell'elenco ordinato per Team sarebbe fatta di celle senza
 * squadra.
 *
 * ⚠️ L'ordinamento legge `riga.squadre` — l'INSIEME dei nomi — e non il testo
 * della cella («Many teams», «All teams»): la cella è resa, la chiave è dato.
 */
const CHIAVE_TUTTE_LE_SQUADRE = "ALL TEAMS";

const chiaveSquadra = (riga) => {
  if (riga.squadre.tutte) return CHIAVE_TUTTE_LE_SQUADRE;

  const ordinati = riga.squadre.nomi
    .map(nome => String(nome).trim().toUpperCase())
    .sort(sortStrings);

  return ordinati.length > 0 ? ordinati[0] : "";
};

const confrontaSquadre = (a, b) => {
  const ca = chiaveSquadra(a);
  const cb = chiaveSquadra(b);

  if (ca === cb) return 0;
  if (ca === "") return 1;
  if (cb === "") return -1;
  return sortStrings(ca, cb);
};

// Il ruolo su cui si ordina è il PRIMO dei ruoli canonici della riga, che
// sono già in ordine alfabetico: COACH prima di PLAYER. Si ordina sul
// canonico e non sull'etichetta perché è il valore, non la sua traduzione, a
// dire che due righe portano lo stesso ruolo.
const chiaveRuolo = (riga) => riga.ruoli[0] || "";

// La classe di stato della cella divisa: verde consegnato, rosso LEGGIBILE
// non consegnato, secondario per «N/A» — la coppia della scheda del socio
// sulle stesse consegne (`MemberProfileModal.css`), portata sulla
// tendina di riga del kit (`.qpsc-select.qpsc-select--…`, AdminTools.css).
const classeStato = (status) => {
  if (status === 'delivered') return 'delivered';
  if (status === 'no') return 'no';
  return 'not_delivered';
};

const UniformDistribution = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  // ⚠️ `saving` era uno stato MORTO: il banner lo leggeva, ma `setSaving` non
  // veniva chiamato da nessuna parte e la scritta «Saving...» non compariva
  // mai. Ora lo scrive `handleUniformChange`, che è l'unica scrittura della
  // pagina.
  const [saving, setSaving] = useState(false);
  // L'avviso della rete di sicurezza: una riga sola, sotto i comandi, che
  // dice che l'ultima modifica non è stata salvata. Sostituisce il NULLA di
  // prima — il PUT senza `try/catch`, che a errore lasciava a schermo un
  // valore che il server non aveva mai ricevuto.
  const [avviso, setAvviso] = useState(null);

  const [sortBy, setSortBy] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showConfig, setShowConfig] = useState(false);
  // La finestra del report «Stats» (Fetta STATS): si apre SOLO al SUPERADMIN,
  // vedi `isSuperAdmin` e la doppia guardia sul pulsante e sul montaggio.
  const [showReport, setShowReport] = useState(false);
  // Il contatore di RICARICA del catalogo (UNIFORMS-UI/Fetta C-config): sale
  // di uno alla chiusura della modale, ed è fra le dipendenze dell'effetto che
  // legge il catalogo, così dopo «Save» + «Close» le colonne nuove si vedono
  // subito, senza ricaricare la pagina. Sale anche a chiusura senza Save: una
  // GET in più, innocua.
  const [ricaricaCatalogo, setRicaricaCatalogo] = useState(0);
  const [config, setConfig] = useState(null);
  const [erroreConfig, setErroreConfig] = useState(null);
  // L'elenco dei soci non letto (D9A): resa come `erroreConfig`, `ud-alert`.
  const [erroreUtenti, setErroreUtenti] = useState(null);

  // ------------------------------------------------------------------
  // LA STAGIONE (UNIFORMS/D3).
  //
  // ⚠️ Questa pagina NON leggeva alcuna stagione: mostrava i tesserati di
  // ogni annata insieme, e il catalogo — che da UNIFORMS/Fetta A vive per
  // anno — non poteva che riferirsi a una sola.
  //
  // ⚠️ IL CANCELLO È QUELLO DELLA SCHEDA DEL SOCIO
  // (`MemberProfileModal.jsx`), e si guardano ENTRAMBI i pezzi:
  // `seasonStatus` può dire "ready" con `operatingSeason` a `null`, perché
  // AuthContext normalizza i due anni in modo indipendente.
  // ------------------------------------------------------------------
  const { user, operatingSeason, enrollmentSeason, seasonStatus } = useAuth() || {};

  // SOLO SUPERADMIN vede il report (UNIFORMS-UI/Fetta STATS, decisione del
  // committente): stesso gate, un ruolo solo — la forma di `isSuperAdmin` in
  // TeamAssignment (il comando LINK). Un ADMIN non superadmin non vede il
  // pulsante (D9A); chi non è admin non arriva nemmeno alla pagina.
  const isSuperAdmin = hasRole(user, "SUPERADMIN");

  const annoOperativo = operatingSeason == null ? null : operatingSeason;
  const stagioneLeggibile = seasonStatus === "ready" && annoOperativo !== null;

  // ⚠️ `undefined` = «non ha ancora scelto», e non è la stessa cosa di una
  // scelta. Il default si CALCOLA (vedi `stagioneAttiva`), non si assegna con
  // un effetto: un default assegnato per effetto va poi difeso da se stesso, e
  // ogni difesa è un punto in cui la scelta dell'amministratore può essere
  // sovrascritta alle sue spalle (UsersList.jsx).
  const [stagioneScelta, setStagioneScelta] = useState(undefined);

  // L'annata che la pagina sta guardando: la scelta se c'è, altrimenti
  // l'anno OPERATIVO. Qui il default non ha ripiego, e non serve: sotto il
  // cancello `stagioneLeggibile` l'anno operativo c'è sempre.
  // (Dichiarata QUI, prima del caricamento del catalogo, perché da UNIFORMS-UI/
  // Fetta B il catalogo si legge per l'anno guardato.)
  const stagioneAttiva =
    stagioneScelta !== undefined ? stagioneScelta : annoOperativo;

  /*
   * IL CATALOGO DELL'ANNO GUARDATO (UNIFORMS-UI/Fetta B, decisione del
   * committente: lettura per anno guardato; dalla Fetta C-config anche la
   * modale di configurazione legge e salva l'anno guardato; dal cantiere
   * UNIFORMS-ANNO/Fetta 3 anche le CONSEGNE dell'elenco si leggono e si
   * scrivono nell'anno guardato, vedi `handleUniformChange`).
   *
   * `GET /uniform-config?season={anno}` — calco della ricarica di Team
   * Assignment al cambio d'anno (`TeamAssignment.jsx`,
   * `GET /teams?season=`): la lettura RIPARTE quando `stagioneAttiva` cambia,
   * e quando la modale si chiude (`ricaricaCatalogo`, Fetta C-config).
   * Prima (Fetta A) il catalogo si leggeva una volta sola e il server
   * rispondeva sempre con l'operativo: guardando il 2027 si vedeva la coorte
   * del 2027 con le colonne e le spettanze del 2026.
   *
   * ⚠️ Il catalogo precedente resta a schermo finché quello nuovo non arriva
   * (nessun «Loading…» che smonti i filtri sotto le dita di chi ha appena
   * cambiato anno); la bandierina `attivo` scarta la risposta di una lettura
   * superata da un cambio d'anno più recente, così una risposta lenta
   * dell'anno prima non sovrascrive quella dell'anno guardato.
   */
  useEffect(() => {
    let attivo = true;

    const fetchConfig = async () => {
      const percorso = Number.isInteger(stagioneAttiva)
        ? `/uniform-config?season=${encodeURIComponent(stagioneAttiva)}`
        : "/uniform-config";

      try {
        const data = await api.get(percorso);
        if (!attivo) return;
        setConfig({
          roles: data?.roles || {},
          teams: data?.teams || {},
          items: Array.isArray(data?.items) ? data.items : null
        });
        setErroreConfig(null);
      } catch (err) {
        if (!attivo) return;

        // ⚠️ I DUE CODICI DELLA FETTA A, distinti sul solo `err.status` che
        // `api.js` allega a ogni risposta non ok (`api/api.js`).
        //
        // 404 = «il catalogo di quest'anno non esiste ancora». NON è un errore:
        // è lo stato ordinario di un'annata nuova, finché un admin non salva la
        // configurazione o non nasce una squadra. Prima faceva `alert("Failed to
        // load uniform config")` all'apertura della pagina, e la pagina restava
        // per sempre su «Loading uniforms...».
        if (err?.status === 404) {
          setConfig(CATALOGO_VUOTO);
          setErroreConfig(null);
          return;
        }

        // 409 = la cassettina della stagione non si legge. È un guasto vero, e
        // si dice con le parole della scheda del socio.
        setConfig(null);
        setErroreConfig(err?.status === 409 ? MSG_STAGIONE : MSG_CONFIG);
      }
    };

    fetchConfig();

    return () => {
      attivo = false;
    };
  }, [stagioneAttiva, ricaricaCatalogo]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // A errore, niente più pop-up `alert` (D9A, RD-168(b)): calco di
  // `fetchUsers` in UsersList — `console.error`, elenco vuoto e una riga a
  // schermo che dice «non sono riuscito a leggere», non «nessun socio».
  const fetchUsers = async () => {
    try {
      const data = await api.get("/users");
      setUsers(Array.isArray(data) ? data : []);
      setErroreUtenti(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
      setErroreUtenti(MSG_UTENTI);
    }
  };

  /*
   * LE ANNATE OFFERTE DALLA TENDINA — corpo di UsersList.
   *
   * Le annate PRESENTI NEI DATI, più i due anni del club: un'annata vuota che
   * si può scegliere dice la verità («nessuno ha ancora rinnovato»),
   * un'annata che non compare non dice niente. Ordine dalla più recente.
   */
  const annateDeiDati = useMemo(
    () =>
      Array.from(
        users.reduce((elenco, u) => {
          const annate = Array.isArray(u?.seasons) ? u.seasons : [];
          annate.forEach((voce) => {
            const anno = annoDiLista(voce);
            if (anno !== null) elenco.add(anno);
          });
          const anno = annoDiLista(u?.season);
          if (anno !== null) elenco.add(anno);
          return elenco;
        }, new Set())
      ).sort((a, b) => b - a),
    [users]
  );

  const stagioniDisponibili = useMemo(
    () =>
      Array.from(
        [operatingSeason, enrollmentSeason].reduce(
          (elenco, anno) => (anno == null ? elenco : elenco.add(anno)),
          new Set(annateDeiDati)
        )
      ).sort((a, b) => b - a),
    [annateDeiDati, operatingSeason, enrollmentSeason]
  );

  // LE CELLE SI TOCCANO SU OGNI ANNO (UNIFORMS-ANNO/Fetta 3, decisione A del
  // committente). Fino alla Fetta 3 qui viveva `solaLettura` — le celle
  // spente fuori dall'operativo — perché le consegne erano UN valore per
  // persona, senz'anno, e scriverle guardando il 2027 avrebbe sovrascritto
  // il 2026. Da quando ogni anno scrive nella sua fetta (`uniformsSeasons`,
  // backend Fetta 1) quella ragione non esiste più: guardando un anno
  // qualsiasi l'admin registra le consegne di QUELL'anno. Al posto del
  // blocco c'è la riga «Editing the … season», sempre visibile.

  /*
   * LE RIGHE DELLA PAGINA — una per persona in elenco, calcolate UNA VOLTA.
   *
   * ⚠️ QUI STA IL PUNTO DELLA FETTA (UNIFORMS/D1, D2). Ogni dato di riga —
   * chi compare, con che ruolo, con che squadra — viene dalle TESSERE VALIDE
   * dell'annata guardata, mai dai campi di persona `user.role` e `user.team`.
   * I sette punti che leggevano `user.team` (chiave delle divise, tendina,
   * filtro, ramo inerte, colonna, ordinamento) leggono ora questa riga.
   *
   * ⚠️ `tessereDelSoggetto` con le opzioni PREDEFINITE del modulo
   * (`utils/squadre.js`): solo tessere ACTIVE, tessera senz'anno esclusa,
   * nessuna amministrativa. Sono esattamente le tre risposte che servono qui
   * — una divisa la riceve chi è tesserato ADESSO, in QUESTA annata, per
   * giocare o per allenare.
   *
   * ⚠️ LE SQUADRE con le opzioni della TENDINA di UsersList:
   * `assorbeTutte: false` — i nomi delle altre tessere restano
   * accanto al permesso «tutte le squadre», altrimenti un coach «all teams»
   * che è anche giocatore dell'U12 toglierebbe «U12» dalla tendina di tutti —
   * e `doppioni: "normalizzati"`, perché «U12» e «u12» sono una voce sola.
   * Il filtro di ruolo (`ruoli`) tiene fuori le squadre di eventuali tessere
   * che in elenco non entrano.
   *
   * ⚠️ UNA PERSONA = UNA RIGA, anche se è player E coach nello stesso anno
   * (UNIFORMS-UI/Fetta A, ponte dichiarato): lo split a due righe è la Fetta
   * C. Su quell'unica riga la colonna Role mostra i due ruoli e la cella Team
   * applica la regola unita (`testoSquadreInCella`).
   */
  const righe = useMemo(() => {
    if (!stagioneLeggibile) return [];

    return users
      .map((user) => {
        const tessere = tessereDelSoggetto(user?.contexts, stagioneAttiva)
          .filter((ctx) => RUOLI_IN_ELENCO.includes(canonicalRole(ctx?.role)));

        if (tessere.length === 0) return null;

        const ruoli = Array.from(
          new Set(tessere.map((ctx) => canonicalRole(ctx?.role)))
        ).sort();

        const squadre = squadreDelSoggetto(user?.contexts, stagioneAttiva, {
          ruoli: RUOLI_IN_ELENCO,
          assorbeTutte: false,
          doppioni: "normalizzati"
        });

        return { user, tessere, ruoli, squadre };
      })
      .filter(Boolean);
  }, [users, stagioneAttiva, stagioneLeggibile]);

  // LE DIVISE CHE SPETTANO, una volta per riga e non una per cella: la sede
  // unica costruisce l'insieme, la riga lo interroga una volta per capo.
  const spettanzePerRiga = useMemo(
    () =>
      new Map(
        righe.map((riga) => [
          riga.user.id,
          tipiDivisaSpettanti(riga.tessere, config)
        ])
      ),
    [righe, config]
  );

  const spettanzeDi = (riga) => spettanzePerRiga.get(riga.user.id) || new Set();

  /*
   * LE VOCI DELLA TENDINA SQUADRE. Non dipendono dai filtri (una tendina che
   * si svuota man mano che si sceglie è una trappola) e nascono dalle righe
   * che la tabella può mostrare, cioè dall'annata guardata.
   *
   * Corpo della tendina squadre di UsersList: doppioni tolti a
   * confronto normalizzato, a schermo la prima grafia incontrata. Niente
   * valori-spia: «ALL» è già diventato `allTeams:true` e non è un nome, e
   * «tutte le squadre» NON è una voce di filtro — è un permesso, e chi ce
   * l'ha corrisponde a ogni voce vera.
   */
  const uniqueTeams = useMemo(
    () =>
      Array.from(
        righe.reduce((elenco, riga) => {
          riga.squadre.nomi.forEach((nome) => {
            const chiave = String(nome).trim().toUpperCase();
            if (!elenco.has(chiave)) elenco.set(chiave, nome);
          });
          return elenco;
        }, new Map()).values()
      ).sort((a, b) =>
        sortStrings(String(a).trim().toUpperCase(), String(b).trim().toUpperCase())
      ),
    [righe]
  );

  /*
   * IL FILTRO. Due condizioni — nome e squadra — e il RAMO INERTE non c'è
   * più: era un ternario che, per i soli PLAYER, ripeteva la stessa
   * condizione sulla squadra già applicata a tutti poche righe sopra —
   * quindi sempre vero. Segnalato da P4/D7 e rimosso (UNIFORMS/D7). Il
   * filtro di ruolo è caduto con la Fetta A di UNIFORMS-UI (punto 5).
   *
   * ⚠️ LA SENTINELLA «Coaches» SI CONTROLLA PRIMA del confronto a maiuscolo
   * coi nomi, come `SENZA_SQUADRA` in Team Assignment: è una
   * condizione sui ruoli della riga, non un nome da cercare fra le squadre.
   *
   * ⚠️ Il coach «tutte le squadre» CORRISPONDE A QUALUNQUE filtro di squadra:
   * criterio di `haLaSquadra` in UsersList, e non per comodità —
   * il suo permesso dice che quelle squadre sono anche le sue. E il filtro
   * legge `riga.squadre`, l'insieme dei nomi, non il testo della cella: chi
   * sta sotto «Many teams» risponde a ciascuna delle sue squadre.
   */
  const righeFiltrate = useMemo(() => {
    const cercato = search.toLowerCase();

    return righe.filter((riga) => {
      const { user, ruoli, squadre } = riga;

      if (cercato && !`${user.firstName} ${user.lastName}`.toLowerCase().includes(cercato)) {
        return false;
      }

      if (selectedTeam === SOLO_COACH) {
        return ruoli.includes("COACH");
      }

      if (selectedTeam) {
        if (squadre.tutte) return true;
        const squadraCercata = selectedTeam.trim().toUpperCase();
        return squadre.nomi.some(
          (nome) => String(nome).trim().toUpperCase() === squadraCercata
        );
      }

      return true;
    });
  }, [righe, search, selectedTeam]);

  /*
   * L'ORDINAMENTO, SU COPIA. `filteredUsers.sort()` ordinava IN LOCO l'array
   * che il filtro aveva appena prodotto; qui si parte da una copia, così il
   * memo di partenza non viene riordinato sotto ai suoi lettori.
   */
  const righeOrdinate = useMemo(() => {
    const verso = sortDirection === 'asc' ? 1 : -1;

    return [...righeFiltrate].sort((a, b) => {
      if (sortBy === 'team') return confrontaSquadre(a, b) * verso;

      let valA = '', valB = '';
      if (sortBy === 'role') {
        valA = chiaveRuolo(a);
        valB = chiaveRuolo(b);
      } else {
        valA = `${a.user.firstName} ${a.user.lastName}`.toLowerCase();
        valB = `${b.user.firstName} ${b.user.lastName}`.toLowerCase();
      }

      return sortStrings(valA, valB) * verso;
    });
  }, [righeFiltrate, sortBy, sortDirection]);

  const handleSort = key => {
    if (sortBy === key) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  /*
   * IL SALVATAGGIO DI UNA CELLA (UNIFORMS/D4, poi UNIFORMS-ANNO/Fetta 3).
   * Tre cambiamenti di D4, e ognuno chiude un difetto suo; più l'ANNO.
   *
   * 1. SI INVIA LA SOLA CELLA TOCCATA, non più l'intera mappa delle divise
   *    della persona. Il backend (Fetta 1) scrive QUELLA cella con un
   *    `FieldPath` a tre segmenti — `uniformsSeasons`, l'anno, l'id del capo
   *    — o a due nel piatto (`uniforms`, id) per il 2026 non battezzato:
   *    le altre celle e gli altri anni restano quelli che sono. Con la mappa
   *    intera due amministratori che lavorassero insieme si sovrascrivevano
   *    a vicenda: l'ultimo a salvare rimandava indietro anche le celle che
   *    l'altro aveva appena cambiato, riportandole al valore che aveva
   *    caricato lui.
   *    ⚠️ La cella si invia INTERA (`status` e `size` insieme): la chiave
   *    inviata SOSTITUISCE quella salvata. Mandare il solo campo cambiato
   *    cancellerebbe l'altro.
   *
   * 2. AGGIORNAMENTO IMMUTABILE. Prima l'oggetto utente si mutava in loco
   *    (`updatedUsers[i].uniforms[type][key] = value` su una copia
   *    SUPERFICIALE dell'array, cioè sugli stessi oggetti): lo stato vecchio e
   *    quello nuovo erano lo stesso oggetto, e nessun lettore poteva accorgersi
   *    del cambiamento se non per il render che il `set` provocava comunque.
   *
   * 3. RETE DI SICUREZZA. Il PUT non aveva `try/catch`: a errore — 403 di chi
   *    non è admin, rete caduta, 500 — la cella restava a schermo col valore
   *    nuovo e il documento conservava il vecchio, senza un segno. Ora la
   *    cella TORNA al valore precedente e una riga lo dice.
   *
   * 4. L'ANNO GUARDATO (Fetta 3). Il PUT dichiara `?season={stagioneAttiva}`
   *    — l'anno viaggia in QUERY, come `PUT /uniform-config?season=` della
   *    modale (`UniformConfig.jsx`); il corpo resta la sola
   *    cella. Il backend scrive nella sede dell'anno (`sedeDelleConsegne`,
   *    server.js) e lo stato locale si aggiorna NELLO STESSO POSTO, chiesto
   *    alla gemella `sedeDelleConsegne` di `utils/uniforms.js`: la fetta
   *    dell'anno, o il piatto se è il 2026 non ancora battezzato. Così, prima
   *    del battesimo, una consegna del 2026 si scrive e si rilegge dal piatto
   *    (coerente col backend, UNIF4-2 C3); dopo, dalla fetta 2026. Nessun
   *    codice speciale: la regola lo copre.
   */
  const handleUniformChange = async (userId, uniformType, key, value) => {
    const utente = users.find(u => u.id === userId);
    if (!utente) return;

    // ⚠️ ANNATA IGNOTA → IL COMANDO NON PARTE (la guardia `annoValido` della
    // finestra Payments): senza un anno la rotta non saprebbe dove scrivere.
    // Sotto il cancello `stagioneLeggibile` non accade; è una guardia di
    // sostanza, come lo era `solaLettura` prima della Fetta 3.
    const sede = sedeDelleConsegne(utente, stagioneAttiva);
    if (sede === null) return;

    const divise = consegneDellAnnata(utente, stagioneAttiva);

    // La cella com'era: si tiene anche la NOTIZIA che non esisteva, così il
    // ripristino non lascia dietro di sé una chiave che prima non c'era. E lo
    // stesso per la FETTA dell'anno: se la mappa per anno non aveva questa
    // chiave, il ripristino non la lascia vuota (il backend, a errore, non
    // l'ha creata).
    const cellaEsisteva = Object.prototype.hasOwnProperty.call(divise, uniformType);
    const fettaEsisteva = !sede.annata || sede.grezzo !== null;
    const precedente = divise[uniformType] || { status: 'not_delivered', size: '' };

    const aggiornata = { ...precedente, [key]: value };

    // Comportamento conservato: passando a «N/A» la taglia si azzera, perché
    // una divisa che non spetta non ha taglia.
    if (key === 'status' && value === 'no') {
      aggiornata.size = '';
    }

    // La mappa per anno della persona in stato, o `{}` (stesso idioma del
    // vecchio `u.uniforms && typeof u.uniforms === 'object'`).
    const mappaPerAnno = (u) =>
      u.uniformsSeasons && typeof u.uniformsSeasons === 'object' && !Array.isArray(u.uniformsSeasons)
        ? u.uniformsSeasons
        : {};

    // Le celle dell'anno RISCRITTE sulla persona, nella sede che la regola
    // indica: la fetta dell'anno (le altre annate intatte) o il piatto.
    const conConsegne = (u, celle) =>
      sede.annata
        ? { ...u, uniformsSeasons: { ...mappaPerAnno(u), [sede.chiave]: celle } }
        : { ...u, uniforms: celle };

    const conCella = (u) =>
      conConsegne(u, { ...consegneDellAnnata(u, stagioneAttiva), [uniformType]: aggiornata });

    const senzaModifica = (u) => {
      const attuali = { ...consegneDellAnnata(u, stagioneAttiva) };
      if (cellaEsisteva) attuali[uniformType] = precedente;
      else delete attuali[uniformType];

      if (!fettaEsisteva && Object.keys(attuali).length === 0) {
        const { [sede.chiave]: fettaNataDalGesto, ...altreAnnate } = mappaPerAnno(u);
        return { ...u, uniformsSeasons: altreAnnate };
      }
      return conConsegne(u, attuali);
    };

    setUsers(prev => prev.map(u => (u.id === userId ? conCella(u) : u)));
    setAvviso(null);
    setSaving(true);

    try {
      // ⚠️ LA RISPOSTA NON SI STENDE SULLO STATO (D9A, RD-168(d), ripiego
      // dichiarato). Il server risponde `{ ok, season, uniforms }` con la
      // FETTA INTERA dell'anno, ma la compone da una lettura fatta PRIMA della
      // scrittura e fuori da una transazione: con due gesti in volo sulla
      // stessa persona (stato poi taglia, o due capi di fila) la risposta del
      // primo può arrivare dopo l'aggiornamento ottimistico del secondo, e
      // stenderla riporterebbe a schermo il valore vecchio di una cella che il
      // server ha invece salvato. La cella toccata, nella risposta, è quella
      // inviata: l'aggiornamento ottimistico la mostra già così.
      await api.put(`/uniform/${userId}?season=${encodeURIComponent(stagioneAttiva)}`, {
        uniforms: { [uniformType]: aggiornata }
      });
    } catch (err) {
      setUsers(prev => prev.map(u => (u.id === userId ? senzaModifica(u) : u)));
      // ⚠️ Il 409 «stagione illeggibile» si distingue dall'errore generico:
      // non è un dato sbagliato, è il club che non si sa in che anno sia, e
      // riprovare fra un momento è la cosa giusta da fare. Dalla Fetta 1
      // (UNIFORMS-ANNO) `PUT /uniform/:id` LO EMETTE davvero: la rotta legge
      // l'anno con `annataFacoltativa` e, a cassettina muta e anno non
      // dichiarato, risponde 409 (UNIF4-2, banco C8). Qui l'anno è sempre
      // dichiarato, quindi il caso è raro; il ramo resta perché il messaggio
      // sia quello giusto.
      setAvviso(err?.status === 409 ? MSG_STAGIONE : MSG_SALVATAGGIO);
    } finally {
      setSaving(false);
    }
  };

  // I CAPI IN COLONNA — UN PUNTO SOLO (UNIFORMS-UI/Fetta A, poi Fetta B):
  // intestazioni, celle ed etichette delle card sotto i 768px nascono tutti
  // da qui. Dalla Fetta B la lista viene dal CATALOGO DELL'ANNO GUARDATO
  // (`config.items`): i soli capi ATTIVI, in ordine di `order`, con la
  // `label` come intestazione e l'`id` come chiave del dato (la cella
  // `consegneDellAnnata(user, anno)[id]`, gli array del catalogo). Un capo spento non è
  // colonna; le sue consegne restano nel dato. Ripiego: `items` null → gli
  // otto capi storici con `id = label = nome` (sede unica,
  // `utils/uniforms.js`), cioè esattamente le colonne di prima.
  const capi = config ? capiAttivi(capiDelCatalogo(config.items)) : [];

  // Il segno del verso dell'ordinamento sull'intestazione toccata: calco di
  // UsersList (`.ul-sort`, oro §1.2), l'unico accento fra le intestazioni.
  const segnoOrdine = (chiave) =>
    sortBy === chiave ? (
      <span className="ud-sort">{sortDirection === 'asc' ? '▲' : '▼'}</span>
    ) : null;

  // ------------------------------------------------------------------
  // I DUE CANCELLI DEL DISEGNO, in quest'ordine.
  //
  // ⚠️ PRIMA LA STAGIONE. Senza l'anno operativo non c'è resa parziale
  // onesta: il predicato dell'anno, con `anno === null`, lascia passare TUTTE
  // le tessere attive di TUTTE le annate (`utils/squadre.js`), e la pagina
  // mostrerebbe un miscuglio di stagioni spacciato per l'anno in corso.
  // Comportamento e testi della scheda del socio
  // (`MemberProfileModal.jsx`), adattati a questa pagina: l'attesa
  // non è un guasto e si dice come attesa.
  //
  // Dalla Fetta A di UNIFORMS-UI anche questo ramo sta DENTRO il telaio, col
  // titolo, come i rami di caricamento ed errore di Team Assignment:
  // il comportamento non cambia — né filtri né tabella —
  // cambia solo che la barra utente e il «Back» ci sono.
  // ------------------------------------------------------------------
  if (!stagioneLeggibile) {
    return (
      <HomeShell backTo="/home" width="admin">
        <h1 className="qpsc-page-title">Uniform Distribution</h1>
        {seasonStatus === "loading" ? (
          <p className="qpsc-note ud-centered">{MSG_ATTESA}</p>
        ) : (
          <p className="ud-alert">{MSG_STAGIONE}</p>
        )}
      </HomeShell>
    );
  }

  return (
  <HomeShell backTo="/home" width="admin">
    {/* Telaio comune (doc 07 §7): barra utente, banda con «‹ Back» e
        «Switch role», colonna degli strumenti admin — a tutta finestra sopra
        i 768px (§8.1), come Team Assignment e User List. Il titolo è la
        classe condivisa (§3.1, centrato in Rosso QPSC) e porta il nome della
        voce di menù delle home Admin e Super Admin. */}
    <h1 className="qpsc-page-title">Uniform Distribution</h1>

    {/* L'elenco dei soci non letto (D9A): la riga rossa della pagina, fuori
        dal ramo del catalogo, così si vede comunque. */}
    {erroreUtenti && <p className="ud-alert">{erroreUtenti}</p>}

    {/* ⚠️ Il catalogo ASSENTE non è più un caso di questo ramo: a 404
        `fetchConfig` mette il catalogo vuoto, e la pagina si disegna con le
        celle tutte vuote — pronta perché la modale lo riempia. Qui restano
        l'attesa vera e il guasto vero. */}
    {erroreConfig ? (
      <p className="ud-alert">{erroreConfig}</p>
    ) : !config ? (
      <p className="qpsc-note ud-centered">{MSG_ATTESA}</p>
    ) : (
      <>
        {/* I PULSANTI DI PAGINA (doc 07 §5; UNIFORMS-UI/Fetta A, punto 6).
            «Team Uniform Config» è l'azione principale: oro §5.1
            (`qpsc-tool-btn`, AdminTools.css). «Stats» (UNIFORMS-UI/Fetta
            STATS) esiste PER IL SOLO SUPERADMIN e apre il report: per l'ADMIN
            non superadmin il pulsante NON SI RENDE affatto (D9A, RD-168(c),
            decisione del committente; prima era spento con « — coming soon »).
            La guardia resta DOPPIA: il gestore non apre nulla se non
            superadmin, e il report non si monta (vedi sotto). Il pulsante
            «Shop» — pop-up «work in process», unica variante d'accento della
            pagina — è stato TOLTO (D9A, RD-168(b), decisione del committente),
            con la sua regola `ud-btn--accent`. Le scritte sono a
            capitalizzazione normale (§3.2): il tutto-maiuscolo è delle sole
            pastiglie. A piena larghezza nella colonna stretta, a misura sopra
            i 768px. */}
        <div className="ud-actions">
          <button
            type="button"
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            onClick={() => setShowConfig(true)}
          >
            Team Uniform Config
          </button>
          {isSuperAdmin && (
            <button
              type="button"
              className="qpsc-tool-btn qpsc-tool-btn--wide"
              onClick={() => {
                if (isSuperAdmin) setShowReport(true);
              }}
            >
              Stats
            </button>
          )}
        </div>

        {/* IL REPORT «Stats» (Fetta STATS): sola vista, calcolato dai dati
            GIÀ in pagina — l'elenco intero di `GET /users` (il predicato
            «giocatore attivo dell'anno» lo applica il modulo puro; la
            ricerca e il filtro squadra della pagina NON lo restringono), il
            catalogo dell'anno guardato, la lista delle taglie. Nessuna
            lettura nuova, nessuna scrittura. Seconda guardia: non si monta
            se lo stato non è superadmin, per qualsiasi via ci si arrivi. */}
        {showReport && isSuperAdmin && (
          <UniformReport
            anno={stagioneAttiva}
            utenti={users}
            config={config}
            sizes={sizes}
            onClose={() => setShowReport(false)}
          />
        )}

        {/* La modale SEGUE L'ANNO GUARDATO e lo scrive nel titolo
            (UNIFORMS-UI/Fetta C-config): l'anno le arriva da qui come
            `stagione` (UNIF3-1 §2.5, opzione i; calco di `TeamManagerModal`
            in TeamAssignment.jsx), non da una sua lettura di `useAuth`. La
            `key` la rimonta da zero a ogni cambio d'anno. Alla chiusura il
            catalogo si rilegge (`ricaricaCatalogo`), così le colonne nuove
            compaiono subito. Il pulsante che la apre è SEMPRE attivo, anche
            su un anno non operativo: sono le CONSEGNE a restare in sola
            lettura fuori dall'operativo, non la configurazione. */}
        {showConfig && (
          <UniformConfig
            key={stagioneAttiva}
            stagione={stagioneAttiva}
            onClose={() => {
              setShowConfig(false);
              setRicaricaCatalogo((v) => v + 1);
            }}
          />
        )}

        <UniformFilters
          search={search}
          setSearch={setSearch}
          stagioneAttiva={stagioneAttiva}
          setStagioneScelta={setStagioneScelta}
          stagioniDisponibili={stagioniDisponibili}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          uniqueTeams={uniqueTeams}
        />

        {/* IL BANNER «Saving…» E L'AVVISO DELLA RETE DI SICUREZZA. Stessa
            posizione e stessa veste di prima (fisso in alto a destra), resi
            a norma: superficie in rilievo, ombra di sistema, testo
            principale; l'avviso — che è un errore — nel rosso LEGGIBILE §1.3,
            come `.ul-msg` di User List. I due non compaiono mai insieme:
            `saving` è già falso quando `avviso` c'è. */}
        {saving && <div className="ud-banner">Saving…</div>}
        {avviso && <div className="ud-banner ud-banner--error">{avviso}</div>}

        {/* L'ANNO IN MODIFICA (UNIFORMS-ANNO/Fetta 3, decisione A): le celle
            si toccano su OGNI anno e ogni anno scrive nella sua fetta, quindi
            l'admin deve vedere a colpo d'occhio su quale anno sta scrivendo.
            La riga è SEMPRE visibile — anche sull'operativo — nella forma
            delle note di stato (`qpsc-note`, stessa veste e stessa posizione
            della nota di sola lettura che sostituisce), subito sotto il
            selettore «Season Viewed» che la governa. Non è la barra di stato
            della stagione (doc 07 §8.2): dice che cosa si sta scrivendo, non
            come sta il club. */}
        <p className="qpsc-note ud-centered">
          Editing the {stagioneAttiva} season: kit changes are saved to this season only.
        </p>

        {/* LA TABELLA (doc 07 §8.1): una `<table>` a tutta finestra sopra i
            768px; sotto, le righe diventano card impilate come in User List
            (`UsersList.css`) — ogni cella porta in `data-label` la
            parola della sua intestazione, e le celle divisa vuote (capo non
            spettante) portano `data-label=""` per non lasciare un'etichetta
            orfana. Le colonne dei capi nascono da `capi`, un
            punto solo. */}
        <div className="ud-table-wrap">
          <table className="ud-table">
            <thead>
              <tr>
                <th className="ud-th--sortable" onClick={() => handleSort('firstName')}>
                  Name{segnoOrdine('firstName')}
                </th>
                <th className="ud-th--sortable" onClick={() => handleSort('role')}>
                  Role{segnoOrdine('role')}
                </th>
                <th className="ud-th--sortable" onClick={() => handleSort('team')}>
                  Team{segnoOrdine('team')}
                </th>
                {capi.map(capo => (
                  <th key={capo.id}>{capo.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {righeOrdinate.map(riga => {
                const user = riga.user;
                const spettanti = spettanzeDi(riga);
                // LE CONSEGNE DELL'ANNO GUARDATO, una volta per riga e non
                // una per cella (UNIFORMS-ANNO/Fetta 3): la sede unica
                // applica la regola «la fetta dell'anno se c'è; altrimenti,
                // solo per il 2026, il piatto; altrimenti niente». Il piatto
                // `user.uniforms` non si legge più direttamente: dopo il
                // battesimo è una fotografia ferma.
                const consegne = consegneDellAnnata(user, stagioneAttiva);

                return (
                <tr key={user.id}>
                  <td data-label="Name">{user.firstName} {user.lastName}</td>
                  {/* ⚠️ RUOLO E SQUADRA VENGONO DALLE TESSERE DELL'ANNATA, non
                      dai campi di persona. Il ruolo si rende con `roleLabel`
                      («Player», non «PLAYER»): prima era `{user.role}` grezzo,
                      quindi a schermo comparivano insieme «Player», «PLAYER» e
                      «Child» per la stessa cosa. Chi ha due tessere — il
                      genitore che allena, il giocatore che è anche assistente —
                      porta due voci, unite dal punto mediano come in User
                      List. */}
                  <td data-label="Role">{riga.ruoli.map(roleLabel).join(' · ') || '-'}</td>
                  {/* La cella Team è SOLA RESA (`testoSquadreInCella`): la
                      squadra del giocatore, le squadre del coach fino a due,
                      «Many teams» da tre, «All teams» col permesso. Filtro e
                      ordinamento leggono `riga.squadre`, non questo testo. */}
                  <td data-label="Team">{testoSquadreInCella(riga.tessere)}</td>
                  {capi.map(capo => {
                    // L'ID è la chiave del dato — quella delle celle di
                    // `consegne` e degli array del catalogo (la spettanza la
                    // costruisce con gli stessi id) — la LABEL è la sola cosa
                    // che si legge.
                    const type = capo.id;

                    if (!spettanti.has(type)) {
                      // Cella vuota, niente menu — e nessuna etichetta sulla
                      // card sotto i 768px.
                      return <td key={type} data-label=""></td>;
                    }

                    // Il ripiego «cella assente = Not Delivered, senza taglia»
                    // resta qui, del chiamante: la sede unica non inventa celle.
                    const uniform =
                      consegne[type] || { status: 'not_delivered', size: '' };
                    const stato = classeStato(uniform.status);

                    // §8.1 deroga al §1.5 (doc 07, voce Uniform Distribution):
                    // il colore CLASSIFICA la consegna — testo della tendina
                    // nel colore dello stato (`.qpsc-select.ud-status--…`,
                    // (0,3,0) con la tabella) e velo di fondo della cella
                    // all'8% (`--qpsc-*-tint`, le fasce di SeasonStatusBar
                    // §8.2).
                    return (
                      <td key={type} data-label={capo.label} className={`ud-cell ud-cell--${stato}`}>
                        <div className="ud-kit">
                          {/* Le tendine sono ACCESE su ogni anno guardato
                              (Fetta 3, decisione A): il gestore scrive nella
                              fetta di quell'anno. */}
                          <select
                            className={`qpsc-select ud-status--${stato}`}
                            aria-label={`${capo.label} status`}
                            value={uniform.status}
                            onChange={e =>
                              handleUniformChange(user.id, type, 'status', e.target.value)
                            }
                          >
                            {statusOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>

                          {/* La taglia: solo se il capo HA una taglia
                              (`hasSize`, Fetta B — una borsa non ne ha) e lo
                              stato non è «N/A». Una taglia già salvata su un
                              capo senza taglia resta nel dato e non si mostra:
                              nessuno la azzera (fuori perimetro). */}
                          {capoHaTaglia(capo) && uniform.status !== 'no' && (
                            <select
                              className="qpsc-select"
                              aria-label={`${capo.label} size`}
                              value={uniform.size || ''}
                              onChange={e =>
                                handleUniformChange(user.id, type, 'size', e.target.value)
                              }
                            >
                              <option value="">Size</option>
                              {sizes.map(size => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    )}
  </HomeShell>
);
};

export default UniformDistribution;
